import { buildFileIndexAsync } from './index.js';
import { buildSymbolIndexAsync } from '../../code-nav/index.js';
import {
  CliSqliteInterrupted,
  CodeNavCacheUnavailableError,
} from '../../sqlite/code-nav-cache/database.js';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { applyIncrementalSymbolIndexUpdates } from './file-index-incremental-symbol-updates.js';

const MAX_INCREMENTAL_SYMBOL_UPDATES = 512;

export type FileIndexWatchRebuildState = {
  pendingSymbolUpdates: Set<string>;
  needsFullSymbolRebuild: boolean;
  rebuildRunning: boolean;
  rebuildQueued: boolean;
};

export function createFileIndexWatchRebuildState(): FileIndexWatchRebuildState {
  return {
    pendingSymbolUpdates: new Set<string>(),
    needsFullSymbolRebuild: false,
    rebuildRunning: false,
    rebuildQueued: false,
  };
}

export function scheduleFileIndexWatchRebuild(
  resolved: string,
  state: FileIndexWatchRebuildState,
  relPath: string | null,
): void {
  if (relPath != null && !state.needsFullSymbolRebuild) {
    state.pendingSymbolUpdates.add(relPath);
    if (state.pendingSymbolUpdates.size > MAX_INCREMENTAL_SYMBOL_UPDATES) {
      state.pendingSymbolUpdates.clear();
      state.needsFullSymbolRebuild = true;
    }
  } else {
    state.pendingSymbolUpdates.clear();
    state.needsFullSymbolRebuild = true;
  }
}

export function runFileIndexWatchRebuild(resolved: string, state: FileIndexWatchRebuildState): void {
  if (isCliImmediateShutdownRequested()) return;
  if (state.rebuildRunning) {
    state.rebuildQueued = true;
    return;
  }
  state.rebuildRunning = true;
  const fullSymbol = state.needsFullSymbolRebuild;
  state.needsFullSymbolRebuild = false;
  const relPaths = fullSymbol ? [] : [...state.pendingSymbolUpdates];
  state.pendingSymbolUpdates.clear();

  void buildFileIndexAsync(resolved)
    .then(async () => {
      if (fullSymbol) {
        await buildSymbolIndexAsync(resolved);
        return;
      }
      if (relPaths.length === 0) return;
      await applyIncrementalSymbolIndexUpdates(resolved, relPaths);
    })
    .catch((e) => {
      if (e instanceof CliSqliteInterrupted) return;
      if (e instanceof CodeNavCacheUnavailableError) return;
      if (isCliImmediateShutdownRequested()) return;
      console.error('[file-index] Watch rebuild failed:', e);
    })
    .finally(() => {
      state.rebuildRunning = false;
      if (state.rebuildQueued || state.needsFullSymbolRebuild || state.pendingSymbolUpdates.size > 0) {
        state.rebuildQueued = false;
        setImmediate(() => runFileIndexWatchRebuild(resolved, state));
      }
    });
}
